import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { ColDef, GridApi, GridReadyEvent, GridOptions } from 'ag-grid-community';
import { AgGridOptionsBuilder, AgGridFilterParams, AgGridColumns } from '../../../Compliance/AgGrid';
import { WeissmanDateFormatPipe, WeissmanTimestampMsecFormatPipe } from '../../../UI-Lib/Pipes/Date-Format/date-formatting.pipe';
import { HistoryLookupService } from '../historyLookup.service';
import { BusyIndicatorService } from '../../../Busy-Indicator';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import * as HistoryLookupGridColumns from './historyLookupGridColumns.util';

@Component({
    selector: 'history-lookup-client-service-responsibility',
    template: `<ws-ag-grid-angular
        class="ag-theme-balham grid grid-cell-no-focus"
        [gridOptions]="gridOptions"
        (gridReady)="onAgGridReady($event)"
        [gridId]="gridId"
        [forceShowToolPanel]="true"
        [showConfigureLayout]="false"
        [defaultSizeToFit]="false"
        [defaultSizeToContent]="true"
    >
    </ws-ag-grid-angular>`,
    standalone: false,
})
export class HistoryLookupClientServiceResponsibilityComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _timestampPipe: WeissmanTimestampMsecFormatPipe,
        private readonly _datePipe: WeissmanDateFormatPipe,
        private readonly _historyLookupService: HistoryLookupService,
        private readonly _busyService: BusyIndicatorService,
        private readonly _toastr: ToastrService,
    ) {}
    @Input({ required: true }) triggerSearch$!: Observable<Core.HistoryLookupSearchModel>;

    private triggerSearchSubscription: Subscription = new Subscription();

    readonly TABLE_NAME: string = 'ClientServiceResponsibility';
    indexedHistoryTableId: number = -1;
    gridId: System.Guid = '59B50791-67A4-4488-8CC6-7AFBB45B4682';
    gridOptions: GridOptions = new AgGridOptionsBuilder()
        .withContext(this)
        .withLoadingOverlay()
        .withMultipleColumnSort()
        .withColumnResize()
        .withTextSelection()
        .build();
    gridApi!: GridApi;
    loadingGrid: boolean = false;
    gridResults: Core.HistoryResultClientServiceResponsibilityModel[] = [];

    async ngOnInit(): Promise<void> {
        this.indexedHistoryTableId =
            this._historyLookupService.tableOptions.find((x) => x.historyTableName === this.TABLE_NAME)
                ?.indexedHistoryTableId ?? -1;

        this.triggerSearchSubscription = this.triggerSearch$.subscribe(
            async (searchModel: Core.HistoryLookupSearchModel) => {
                await this.doSearch(searchModel);
            },
        );
    }

    ngOnDestroy(): void {
        if (this.triggerSearchSubscription) {
            this.triggerSearchSubscription.unsubscribe();
        }
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;

        const columns: ColDef[] = this.getGridColumns();
        this.gridApi.setGridOption('columnDefs', columns);

        this.gridApi.setGridOption('loading', false);
        this.gridApi.setGridOption('rowData', []);
    }

    readyToSearch(searchModel: Core.HistoryLookupSearchModel): boolean {
        if (
            searchModel.indexedHistoryTableId <= 0 ||
            searchModel.indexedHistoryColumnId <= 0 ||
            !searchModel.columnIdValues
        ) {
            return false;
        }

        return searchModel.columnIdValues.every((value) => value > 0);
    }

    async doSearch(searchModel: Core.HistoryLookupSearchModel): Promise<void> {
        if (!this.readyToSearch(searchModel)) {
            this._toastr.error(
                'Please select a table and column, and enter values for all columns before searching.',
                'Cannot Search',
            );
            return;
        }
        if (searchModel.indexedHistoryTableId !== this.indexedHistoryTableId) {
            //console.warn(`ClientServiceResponsibility.doSearch() called for Table ${searchModel.indexedHistoryTableId}, expected ${this.indexedHistoryTableId}.`);
            return;
        }
        if (!this.gridApi) {
            //console.warn('GridApi not ready yet');
            return;
        }

        this.loadingGrid = true;
        this.gridApi.setGridOption('loading', true);

        const busyRef = this._busyService.show({ message: 'Loading' });

        try {
            this.gridResults = await this._historyLookupService.historyLookupClientServiceResponsibility(searchModel);

            this._refreshRowData();
        } catch (error) {
            if (error.status !== 403) {
                this._toastr.error('An unexpected error occurred while fetching history data.', 'Search Error');
            }
            console.warn(error);
        } finally {
            busyRef.hide();
            this.gridApi.setGridOption('loading', false);
            this.loadingGrid = false;
        }
    }

    private _refreshRowData(): void {
        if (!this.gridApi) {
            return;
        }

        const columns = this.getGridColumns();
        this.gridApi.setGridOption('columnDefs', columns);

        this.gridApi.setGridOption('rowData', this._getRowData());
    }

    private _getRowData(): Core.HistoryResultClientServiceResponsibilityModel[] {
        return _.chain(this.gridResults).cloneDeep().orderBy(['historyID'], ['desc']).value();
    }

    private getGridColumns(): ColDef[] {
        let cols = this._historyLookupService.getBaseHistoryColumnDefs();

        cols = [...cols, ...this._getSpecificColumnDefs()];

        return cols;
    }

    private _getSpecificColumnDefs(): ColDef[] {
        const columns: ColDef[] = [
            HistoryLookupGridColumns.numberColumn('ClientServiceResponsibilityID', 'clientServiceResponsibilityID'),
            HistoryLookupGridColumns.numberColumn('EntityTypeID', 'entityTypeID'),
            HistoryLookupGridColumns.numberColumn('EntityID', 'entityID'),
            HistoryLookupGridColumns.numberColumn('ClientServiceID', 'clientServiceID'),
            HistoryLookupGridColumns.numberColumn('PropertyTypeID', 'propertyTypeID'),
            HistoryLookupGridColumns.numberColumn('WorkflowUserTypeID', 'workflowUserTypeID'),
            HistoryLookupGridColumns.textColumn('UserID', 'userID'),
            HistoryLookupGridColumns.numberColumn('TeamID', 'teamID'),
            HistoryLookupGridColumns.numberColumn('ConsultingEngagementID', 'consultingEngagementID'),
            HistoryLookupGridColumns.dateColumn<Core.HistoryResultClientServiceResponsibilityModel>(
                'DateFrom',
                'dateFrom',
                this._datePipe,
            ),
            HistoryLookupGridColumns.textColumn('ActivityCorrelationID', 'activityCorrelationId'),
            HistoryLookupGridColumns.timestampColumn<Core.HistoryResultClientServiceResponsibilityModel>(
                'ChangeDate',
                'changeDate',
                this._timestampPipe,
            ),
            HistoryLookupGridColumns.textColumn('ChangedBy', 'changedBy'),
        ];

        return columns;
    }
}
